import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Mic, MicOff, Volume2, VolumeX, Send, ChevronRight,
    CheckCircle2, AlertTriangle, Eye, EyeOff, Award, IndianRupee,
    Loader2, Sparkles, XCircle, StopCircle,
} from 'lucide-react';
import api from '../lib/api';
import { ROUNDS } from '../lib/constants';
import { useSpeechRecognition, speakText, stopSpeaking } from '../hooks/useVoice';

const scoreColor = (score) => {
    if (score >= 8) return { text: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', ring: '#10B981' };
    if (score >= 5) return { text: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', ring: '#F59E0B' };
    return { text: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', ring: '#EF4444' };
};

const stageIntro = {
    QUESTION: 'question',
    EVAL: 'eval',
    ROUND_DONE: 'round_done',
    SALARY_OFFER: 'salary_offer',
    SALARY_RESULT: 'salary_result',
    REJECTED: 'rejected',
    DONE: 'done',
};

export default function Interview() {
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const [roundNum, setRoundNum] = useState(1);
    const [questions, setQuestions] = useState([]);
    const [qIndex, setQIndex] = useState(0);
    const [answer, setAnswer] = useState('');
    const [evalResult, setEvalResult] = useState(null);
    const [showExpected, setShowExpected] = useState(false);
    const [stage, setStage] = useState(stageIntro.QUESTION);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');
    const [ttsOn, setTtsOn] = useState(true);

    // Salary round state
    const [salaryOffer, setSalaryOffer] = useState(null); // {offered_salary, negotiation_message}
    const [salaryResult, setSalaryResult] = useState(null); // {final_salary, hr_response}
    const [rejected, setRejected] = useState(null);
    const [negotiation, setNegotiation] = useState('');

    // Round scores tracker
    const [roundScores, setRoundScores] = useState({}); // { 1: [s1,s2,s3], ... }

    // End-interview confirmation modal
    const [showEndConfirm, setShowEndConfirm] = useState(false);
    const [ending, setEnding] = useState(false);

    const stt = useSpeechRecognition();

    // Start the current round on mount / round change
    useEffect(() => {
        (async () => {
            setLoading(true);
            setErr('');
            try {
                if (roundNum === 5) {
                    const res = await api.post('/interviews/round/start', {
                        session_id: sessionId,
                        round_number: 5,
                    });
                    if (res.data.rejected) {
                        setRejected(res.data.rejection_reason);
                        setStage(stageIntro.REJECTED);
                    } else {
                        setSalaryOffer({
                            offered_salary: res.data.offered_salary,
                            negotiation_message: res.data.negotiation_message,
                        });
                        setStage(stageIntro.SALARY_OFFER);
                        if (ttsOn) speakText(res.data.negotiation_message);
                    }
                } else {
                    const res = await api.post('/interviews/round/start', {
                        session_id: sessionId,
                        round_number: roundNum,
                    });
                    setQuestions(res.data.questions || []);
                    setQIndex(0);
                    setAnswer('');
                    setEvalResult(null);
                    setShowExpected(false);
                    setStage(stageIntro.QUESTION);
                    stt.reset();
                    if (ttsOn && res.data.questions?.[0]) speakText(res.data.questions[0].text);
                }
            } catch (e) {
                setErr(e.response?.data?.detail || 'Failed to start round');
            } finally {
                setLoading(false);
            }
        })();
        return () => stopSpeaking();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roundNum, sessionId]);

    // Sync STT transcript into answer
    useEffect(() => {
        if (stt.transcript && stage === stageIntro.QUESTION) {
            setAnswer(stt.transcript);
        }
    }, [stt.transcript, stage]);

    const currentQ = questions[qIndex];
    const roundMeta = ROUNDS.find((r) => r.num === roundNum);

    const submitAnswer = async () => {
        if (!answer.trim() && !confirm('Submit an empty answer?')) return; // eslint-disable-line no-alert
        setLoading(true);
        setErr('');
        stopSpeaking();
        stt.stop();
        try {
            const res = await api.post('/interviews/answer', {
                session_id: sessionId,
                round_number: roundNum,
                question_index: qIndex,
                answer,
            });
            setEvalResult(res.data.evaluation);
            setStage(stageIntro.EVAL);
            setRoundScores((prev) => ({
                ...prev,
                [roundNum]: [...(prev[roundNum] || []), res.data.evaluation.score],
            }));
            if (ttsOn) speakText(res.data.evaluation.feedback);
        } catch (e) {
            setErr(e.response?.data?.detail || 'Failed to evaluate');
        } finally {
            setLoading(false);
        }
    };

    const nextQuestion = () => {
        stopSpeaking();
        if (qIndex + 1 >= questions.length) {
            setStage(stageIntro.ROUND_DONE);
            return;
        }
        setQIndex((i) => i + 1);
        setAnswer('');
        setEvalResult(null);
        setShowExpected(false);
        stt.reset();
        setStage(stageIntro.QUESTION);
        if (ttsOn && questions[qIndex + 1]) speakText(questions[qIndex + 1].text);
    };

    const nextRound = () => {
        setRoundNum((r) => r + 1);
    };

    const submitNegotiation = async () => {
        setLoading(true);
        setErr('');
        stopSpeaking();
        stt.stop();
        try {
            const res = await api.post('/interviews/salary/negotiate', {
                session_id: sessionId,
                negotiation_response: negotiation || 'I accept the offer.',
            });
            setSalaryResult(res.data);
            setStage(stageIntro.SALARY_RESULT);
            if (ttsOn) speakText(res.data.hr_response);
        } catch (e) {
            setErr(e.response?.data?.detail || 'Failed to submit negotiation');
        } finally {
            setLoading(false);
        }
    };

    const finishInterview = async () => {
        setLoading(true);
        try {
            await api.post(`/interviews/complete?session_id=${sessionId}`);
            navigate(`/results/${sessionId}`);
        } catch (e) {
            setErr(e.response?.data?.detail || 'Failed to finalize');
        } finally {
            setLoading(false);
        }
    };

    const abandonInterview = async () => {
        setEnding(true);
        stopSpeaking();
        stt.stop();
        try {
            await api.post(`/interviews/abandon?session_id=${sessionId}`);
        } catch (e) {
            // Even if it fails, still navigate away — nothing useful the user can do here
            // eslint-disable-next-line no-console
            console.warn('abandon failed', e);
        } finally {
            setEnding(false);
            navigate('/home');
        }
    };

    const roundAvg = useMemo(() => {
        const list = roundScores[roundNum] || [];
        return list.length ? (list.reduce((a, b) => a + b, 0) / list.length).toFixed(1) : null;
    }, [roundScores, roundNum]);

    // Toggle voice recording
    const toggleMic = () => {
        if (!stt.supported) {
            alert('Speech recognition not supported in this browser. Please use Chrome/Edge.'); // eslint-disable-line no-alert
            return;
        }
        if (stt.isListening) {
            stt.stop();
        } else {
            stt.reset();
            setAnswer('');
            stt.start();
        }
    };

    const toggleTts = () => {
        if (ttsOn) stopSpeaking();
        setTtsOn((v) => !v);
    };

    const totalQuestions = questions.length || 3;
    const progressPct = stage === stageIntro.EVAL
        ? ((qIndex + 1) / totalQuestions) * 100
        : (qIndex / totalQuestions) * 100;

    return (
        <div className="space-y-6" data-testid="interview-page">
            {/* Round header */}
            <div className="brand-card p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <div className="text-xs font-bold uppercase tracking-wide text-[#1C3A94]">
                            Round {roundNum} of 5
                        </div>
                        <div className="text-lg font-bold text-[#111827]">
                            {roundMeta?.name || 'Interview'}
                        </div>
                        <div className="text-xs text-[#6B7280]">{roundMeta?.desc}</div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            data-testid="tts-toggle"
                            onClick={toggleTts}
                            className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition ${
                                ttsOn
                                    ? 'border-[#1C3A94] bg-[#F1F5FD] text-[#1C3A94] hover:bg-[#E4EBFC]'
                                    : 'border-[#E5E7EB] bg-white text-[#6B7280] hover:bg-gray-50'
                            }`}
                            title={ttsOn ? 'Mute the AI interviewer voice' : 'Unmute the AI interviewer voice'}
                            aria-pressed={ttsOn}
                        >
                            {ttsOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                            <span>{ttsOn ? 'Voice on' : 'Voice muted'}</span>
                        </button>
                        <button
                            data-testid="end-interview-btn"
                            onClick={() => setShowEndConfirm(true)}
                            className="inline-flex items-center gap-2 rounded-lg border border-[#E52C2C] bg-white px-3 py-2 text-sm font-semibold text-[#E52C2C] transition hover:bg-[#FEF2F2]"
                            title="Stop and exit the interview — your progress will be discarded"
                        >
                            <StopCircle className="h-4 w-4" />
                            <span>End interview</span>
                        </button>
                    </div>
                </div>

                {/* Round pills */}
                <div className="mt-4 flex flex-wrap gap-1.5">
                    {ROUNDS.map((r) => (
                        <div
                            key={r.num}
                            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                                r.num < roundNum
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : r.num === roundNum
                                    ? 'bg-[#1C3A94] text-white'
                                    : 'bg-gray-100 text-[#9CA3AF]'
                            }`}
                        >
                            {r.num < roundNum && <CheckCircle2 className="h-3 w-3" />}
                            {r.num}. {r.name}
                        </div>
                    ))}
                </div>

                {roundNum <= 4 && (
                    <div className="mt-4">
                        <div className="mb-1 flex justify-between text-[11px] font-semibold text-[#6B7280]">
                            <span>Question {Math.min(qIndex + 1, totalQuestions)} of {totalQuestions}</span>
                            <span>Round avg: {roundAvg || '—'}</span>
                        </div>
                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                            <motion.div
                                className="h-full bg-gradient-to-r from-[#1C3A94] to-[#3552B8]"
                                initial={{ width: 0 }}
                                animate={{ width: `${progressPct}%` }}
                                transition={{ duration: 0.4 }}
                            />
                        </div>
                    </div>
                )}
            </div>

            {err && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
                    {err}
                </div>
            )}

            {loading && (
                <div className="brand-card p-10 flex items-center justify-center gap-3 text-[#6B7280]">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span className="text-sm">Working with the AI…</span>
                </div>
            )}

            {/* MAIN STAGES */}
            <AnimatePresence mode="wait">
                {!loading && stage === stageIntro.QUESTION && currentQ && (
                    <motion.div
                        key="question"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        className="brand-card p-6 md:p-8"
                        data-testid="question-card"
                    >
                        <div className="text-xs font-bold uppercase tracking-wide text-[#E52C2C]">
                            Question {qIndex + 1}
                        </div>
                        <h2 className="mt-2 text-xl md:text-2xl font-bold text-[#111827] leading-snug">
                            {currentQ.text}
                        </h2>

                        <div className="mt-6">
                            <label className="mb-2 flex items-center justify-between">
                                <span className="text-xs font-semibold text-[#111827]">Your answer</span>
                                <button
                                    data-testid="mic-toggle"
                                    onClick={toggleMic}
                                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition ${
                                        stt.isListening
                                            ? 'bg-[#E52C2C] text-white'
                                            : 'bg-[#F1F5FD] text-[#1C3A94] hover:bg-[#E4EBFC]'
                                    }`}
                                >
                                    {stt.isListening ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
                                    {stt.isListening ? 'Stop recording' : 'Record voice'}
                                </button>
                            </label>
                            <textarea
                                data-testid="answer-textarea"
                                value={answer}
                                onChange={(e) => setAnswer(e.target.value)}
                                placeholder="Type or record your answer here…"
                                rows={7}
                                className="brand-input resize-y min-h-[140px]"
                            />
                            {stt.error && (
                                <div className="mt-2 text-xs text-red-600">
                                    Voice error: {stt.error}
                                </div>
                            )}
                        </div>

                        <div className="mt-6 flex justify-end">
                            <button
                                data-testid="submit-answer-btn"
                                onClick={submitAnswer}
                                className="brand-btn-primary gap-2"
                                disabled={loading}
                            >
                                <Send className="h-4 w-4" />
                                Submit answer
                            </button>
                        </div>
                    </motion.div>
                )}

                {!loading && stage === stageIntro.EVAL && evalResult && (
                    <motion.div
                        key="eval"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        data-testid="evaluation-card"
                        className="space-y-4"
                    >
                        <EvaluationCard
                            score={evalResult.score}
                            feedback={evalResult.feedback}
                            strengths={evalResult.strengths}
                            improvements={evalResult.improvements}
                            expectedAnswer={evalResult.expected_answer}
                            showExpected={showExpected}
                            toggleExpected={() => setShowExpected((v) => !v)}
                        />
                        <div className="flex justify-end">
                            <button
                                data-testid="next-question-btn"
                                onClick={nextQuestion}
                                className="brand-btn-primary gap-2"
                            >
                                {qIndex + 1 >= questions.length ? 'Finish round' : 'Next question'}
                                <ChevronRight className="h-4 w-4" />
                            </button>
                        </div>
                    </motion.div>
                )}

                {!loading && stage === stageIntro.ROUND_DONE && (
                    <motion.div
                        key="round_done"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        data-testid="round-done-card"
                        className="brand-card p-8 text-center"
                    >
                        <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                            <CheckCircle2 className="h-7 w-7" />
                        </div>
                        <h2 className="mt-3 text-2xl font-bold text-[#111827]">
                            Round {roundNum} complete!
                        </h2>
                        <p className="mt-1 text-sm text-[#6B7280]">
                            Round average: <span className="font-bold text-[#111827]">{roundAvg || '—'}</span> / 10
                        </p>
                        <button
                            data-testid="start-next-round-btn"
                            onClick={nextRound}
                            className="brand-btn-primary mt-6 gap-2"
                        >
                            {roundNum === 4 ? 'Go to Salary Negotiation' : `Start Round ${roundNum + 1}`}
                            <ChevronRight className="h-4 w-4" />
                        </button>
                    </motion.div>
                )}

                {/* Salary offer */}
                {!loading && stage === stageIntro.SALARY_OFFER && salaryOffer && (
                    <motion.div
                        key="salary_offer"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        data-testid="salary-offer-card"
                        className="space-y-4"
                    >
                        <div className="brand-card p-6 md:p-8 border-2 border-emerald-200 bg-emerald-50/40">
                            <div className="flex items-start gap-4">
                                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-500 text-white">
                                    <IndianRupee className="h-6 w-6" />
                                </div>
                                <div className="flex-1">
                                    <div className="text-xs font-bold uppercase tracking-wide text-emerald-700">
                                        Congratulations — Salary Offer
                                    </div>
                                    <div className="mt-2 text-4xl md:text-5xl font-extrabold text-[#111827]">
                                        ₹{salaryOffer.offered_salary} <span className="text-lg font-semibold text-[#6B7280]">LPA</span>
                                    </div>
                                    <p className="mt-3 text-sm text-[#111827] leading-relaxed">
                                        {salaryOffer.negotiation_message}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="brand-card p-6">
                            <div className="text-xs font-bold uppercase tracking-wide text-[#1C3A94]">
                                Your Negotiation
                            </div>
                            <label className="mt-3 mb-2 flex items-center justify-between">
                                <span className="text-xs font-semibold text-[#111827]">
                                    Justify a higher offer (skills, experience, market benchmarks)
                                </span>
                                <button
                                    data-testid="mic-toggle-salary"
                                    onClick={toggleMic}
                                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
                                        stt.isListening
                                            ? 'bg-[#E52C2C] text-white'
                                            : 'bg-[#F1F5FD] text-[#1C3A94] hover:bg-[#E4EBFC]'
                                    }`}
                                >
                                    {stt.isListening ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
                                    {stt.isListening ? 'Stop' : 'Record'}
                                </button>
                            </label>
                            <textarea
                                data-testid="negotiation-textarea"
                                value={negotiation || stt.transcript}
                                onChange={(e) => { setNegotiation(e.target.value); stt.setTranscript(e.target.value); }}
                                placeholder="e.g. Given my background in SQL, Python, and dashboarding, along with market benchmarks in Bangalore for Data Analyst roles at ₹X LPA, I would like to request ₹Y LPA…"
                                rows={6}
                                className="brand-input resize-y"
                            />
                            <div className="mt-4 flex justify-end">
                                <button
                                    data-testid="submit-negotiation-btn"
                                    onClick={submitNegotiation}
                                    className="brand-btn-primary gap-2"
                                >
                                    <Send className="h-4 w-4" />
                                    Submit negotiation
                                </button>
                            </div>
                        </div>
                    </motion.div>
                )}

                {!loading && stage === stageIntro.SALARY_RESULT && salaryResult && (
                    <motion.div
                        key="salary_result"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        data-testid="salary-result-card"
                        className="brand-card p-8 border-2 border-[#1C3A94]/20"
                    >
                        <div className="text-xs font-bold uppercase tracking-wide text-[#1C3A94]">
                            Final Offer
                        </div>
                        <div className="mt-2 text-5xl md:text-6xl font-extrabold text-[#111827]">
                            ₹{salaryResult.final_salary} <span className="text-xl font-semibold text-[#6B7280]">LPA</span>
                        </div>
                        <p className="mt-4 text-[#111827] leading-relaxed">
                            <span className="font-semibold">HR: </span>
                            {salaryResult.hr_response}
                        </p>
                        <button
                            data-testid="finish-interview-btn"
                            onClick={finishInterview}
                            className="brand-btn-primary mt-8 gap-2"
                        >
                            <Award className="h-4 w-4" />
                            View overall results
                        </button>
                    </motion.div>
                )}

                {!loading && stage === stageIntro.REJECTED && (
                    <motion.div
                        key="rejected"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        data-testid="rejected-card"
                        className="brand-card p-8 border-2 border-red-200 bg-red-50/40"
                    >
                        <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-red-500 text-white">
                            <XCircle className="h-6 w-6" />
                        </div>
                        <h2 className="mt-3 text-2xl font-bold text-[#111827]">
                            Interview not progressed
                        </h2>
                        <p className="mt-3 text-[#111827] leading-relaxed">{rejected}</p>
                        <button
                            onClick={finishInterview}
                            className="brand-btn-outline mt-6 gap-2"
                        >
                            View summary
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* End Interview confirmation modal */}
            <AnimatePresence>
                {showEndConfirm && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
                        onClick={() => !ending && setShowEndConfirm(false)}
                        data-testid="end-confirm-modal"
                    >
                        <motion.div
                            onClick={(e) => e.stopPropagation()}
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: 20, opacity: 0 }}
                            className="w-full max-w-md brand-card p-6 md:p-8"
                        >
                            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-red-600">
                                <StopCircle className="h-6 w-6" />
                            </div>
                            <h3 className="mt-4 text-xl font-extrabold text-[#111827]">
                                End interview now?
                            </h3>
                            <p className="mt-2 text-sm text-[#6B7280] leading-relaxed">
                                Your progress in this session will be discarded. No score or feedback will be
                                saved. You can always start a new interview from the home page.
                            </p>
                            <div className="mt-6 flex justify-end gap-2">
                                <button
                                    data-testid="end-cancel-btn"
                                    type="button"
                                    onClick={() => setShowEndConfirm(false)}
                                    disabled={ending}
                                    className="brand-btn-outline"
                                >
                                    Keep going
                                </button>
                                <button
                                    data-testid="end-confirm-btn"
                                    type="button"
                                    onClick={abandonInterview}
                                    disabled={ending}
                                    className="brand-btn-secondary gap-2"
                                >
                                    {ending ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Ending…
                                        </>
                                    ) : (
                                        <>
                                            <StopCircle className="h-4 w-4" />
                                            End interview
                                        </>
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

function EvaluationCard({ score, feedback, strengths, improvements, expectedAnswer, showExpected, toggleExpected }) {
    const c = scoreColor(score);
    return (
        <div className={`brand-card p-6 md:p-8 border ${c.border}`}>
            <div className="flex items-start gap-5">
                <div
                    className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-4"
                    style={{ borderColor: c.ring }}
                >
                    <div className="text-center">
                        <div className={`text-2xl font-extrabold ${c.text}`}>{score}</div>
                        <div className="text-[10px] font-semibold text-[#9CA3AF]">/ 10</div>
                    </div>
                </div>
                <div className="flex-1">
                    <div className={`inline-flex items-center gap-1.5 rounded-full ${c.bg} px-3 py-1 text-xs font-bold ${c.text}`}>
                        <Sparkles className="h-3 w-3" /> AI Evaluation
                    </div>
                    <p className="mt-3 text-[#111827] leading-relaxed">{feedback}</p>
                </div>
            </div>

            {(strengths?.length > 0 || improvements?.length > 0) && (
                <div className="mt-6 grid md:grid-cols-2 gap-4">
                    {strengths?.length > 0 && (
                        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                            <div className="mb-2 text-xs font-bold uppercase tracking-wide text-emerald-700">
                                Strengths
                            </div>
                            <ul className="space-y-1.5">
                                {strengths.map((s, i) => (
                                    <li key={i} className="flex items-start gap-2 text-sm text-emerald-900">
                                        <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-emerald-600" />
                                        <span>{s}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {improvements?.length > 0 && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                            <div className="mb-2 text-xs font-bold uppercase tracking-wide text-amber-700">
                                Improvements
                            </div>
                            <ul className="space-y-1.5">
                                {improvements.map((s, i) => (
                                    <li key={i} className="flex items-start gap-2 text-sm text-amber-900">
                                        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
                                        <span>{s}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {expectedAnswer && (
                <div className="mt-6 border-t border-[#E5E7EB] pt-4">
                    <button
                        data-testid="toggle-expected-answer-btn"
                        onClick={toggleExpected}
                        className="brand-btn-ghost gap-1.5"
                    >
                        {showExpected ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        {showExpected ? 'Hide expected answer' : 'Show expected answer'}
                    </button>
                    {showExpected && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            className="mt-3 rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] p-4 text-sm text-[#111827] leading-relaxed"
                        >
                            {expectedAnswer}
                        </motion.div>
                    )}
                </div>
            )}
        </div>
    );
}
